import React from 'react'
import { getUnixTime } from 'date-fns/getUnixTime'

import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import {
  ANALYTICS_EVENTS,
  Calendar,
  CalendarSelectionTypes,
  Drawer,
  useEvent,
  useGlobalCopyStore,
  useTransactionStore,
} from '../../../../../common'

import { DATE_FORMATS_INTL, formatDate } from '../../../../../core/constants'
import { fromUnixUTC } from '../../../../../core/utils/DateUtil'
import type { Transaction } from '../../../../../core'

import { useTransactionsUiStore } from '../../../../context/hooks'
import { ON_CLICK_EVENTS } from '../../../../constants/OnClickEvents'

interface DateActionProps {
  transaction: Transaction
}

const DateAction: React.FC<DateActionProps> = ({ transaction }) => {
  const { onEvent } = useEvent()
  const { updateTransaction } = useTransactionStore()
  const { updateManualTransaction } = useTransactionsUiStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()

  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<number>(transaction.date)

  const handleDateSelected = (date: Date) => {
    setDate(getUnixTime(date))
  }

  const handleSave = async () => {
    if (transaction.is_manual && !transaction.guid) {
      updateManualTransaction({ ...transaction, date })
      setOpen(false)
      return
    }

    await updateTransaction({ ...transaction, date })
    onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_CLICK_DATE, { transaction_guid: transaction.guid })
    setOpen(false)
  }

  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  return (
    <ListItem className="mx-txn-date-action" disableGutters={true} disablePadding={true}>
      <ListItemButton
        onClick={() => {
          onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_DATE_CLICK)
          setOpen(true)
        }}
      >
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: bgColor }} variant="rounded">
            <Icon name="calendar_month" sx={{ color: 'text.primary' }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={copy.date_title}
          secondary={
            <Text bold={true} sx={{ color: 'text.primary' }} variant="body1">
              {formatDate(fromUnixUTC(transaction.date), DATE_FORMATS_INTL.FULL_MONTH_DAY_YEAR)}
            </Text>
          }
          slotProps={{ primary: { variant: 'caption', fontSize: 11, fontWeight: 'normal' } }}
        />
        <ListItemIcon>
          <Icon name="chevron_right" sx={{ color: 'text.primary' }} />
        </ListItemIcon>
      </ListItemButton>

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={open}
        onClose={() => setOpen(false)}
        onPrimaryAction={handleSave}
        primaryText={commonCopy.save_button}
        secondaryText={commonCopy.cancel_button}
        title={copy.date_edit_title}
      >
        <Stack alignItems="center" mt={40}>
          <Calendar
            beginDate={fromUnixUTC(date)}
            copy={{
              nextAria: copy.date_next_aria,
              prevAria: copy.date_prev_aria,
              today: commonCopy.date_range_picker.today,
            }}
            onDateSelected={handleDateSelected}
            selectionType={CalendarSelectionTypes.Single}
          />
        </Stack>
      </Drawer>
    </ListItem>
  )
}

export default DateAction
